(() => {
  const DESCRIPTION_ROOT_SELECTOR = '[data-collection-description-root]';
  const FAQ_ROOT_SELECTOR = '[data-collection-faq-source]';
  const FOLD_ITEM_SELECTOR = '.CD-fold-item';
  const FOLD_TITLE_SELECTOR = '.CD-fold-title';
  const FOLD_CONTENT_SELECTOR = '.CD-fold-content';
  const DESCRIPTION_TOGGLE_CLASS = 'collection-description-content__toggle';
  let descriptionIdCounter = 0;

  const normalizeExternalLinks = (container) => {
    container.querySelectorAll('a').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.href);

        if (url.origin !== window.location.origin) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        }
      } catch (error) {
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
  };

  const getDirectChild = (parent, selector) => {
    return Array.from(parent.children).find((child) => child.matches(selector)) || null;
  };

  const isTopLevelFoldItem = (item) => {
    return !item.parentElement || !item.parentElement.closest(FOLD_ITEM_SELECTOR);
  };

  const createDescriptionContentPanel = (content, className = 'collection-description-accordion__content') => {
    const panel = document.createElement('div');
    panel.className = className;

    Array.from(content.childNodes).forEach((node) => {
      panel.appendChild(node.cloneNode(true));
    });

    normalizeExternalLinks(panel);

    return panel;
  };

  const createDescriptionAccordionItem = (item) => {
    const title = getDirectChild(item, FOLD_TITLE_SELECTOR);
    const content = getDirectChild(item, FOLD_CONTENT_SELECTOR);

    if (!title || !content) {
      return null;
    }

    const details = document.createElement('details');
    details.className = 'collection-description-accordion__item';

    const summary = document.createElement('summary');
    summary.className = 'collection-description-accordion__summary';
    summary.innerHTML = title.innerHTML;

    const panel = createDescriptionContentPanel(content);

    details.append(summary, panel);

    return details;
  };

  const initDescriptionAccordion = (root) => {
    if (!root || root.dataset.collectionDescriptionAccordionReady === 'true') {
      return;
    }

    const items = Array.from(root.querySelectorAll(FOLD_ITEM_SELECTOR)).filter(isTopLevelFoldItem);
    let transformedCount = 0;

    items.forEach((item) => {
      const accordionItem = createDescriptionAccordionItem(item);

      if (!accordionItem) {
        return;
      }

      item.replaceWith(accordionItem);
      transformedCount += 1;
    });

    if (transformedCount > 0) {
      root.classList.add('collection-description-root--has-accordion');
    }

    normalizeExternalLinks(root);
    root.dataset.collectionDescriptionAccordionReady = 'true';
  };

  const getDescriptionLineHeight = (root) => {
    const styles = window.getComputedStyle(root);
    const lineHeight = Number.parseFloat(styles.lineHeight);

    if (Number.isFinite(lineHeight)) {
      return lineHeight;
    }

    const fontSize = Number.parseFloat(styles.fontSize);
    return Number.isFinite(fontSize) ? fontSize * 1.7 : 0;
  };

  const createDescriptionToggle = (root) => {
    const toggle = document.createElement('button');
    const viewport = root.dataset.descriptionViewport || 'shared';

    descriptionIdCounter += 1;
    root.id ||= `CollectionDescription-${descriptionIdCounter}`;

    toggle.type = 'button';
    toggle.className = DESCRIPTION_TOGGLE_CLASS;
    toggle.hidden = true;
    toggle.dataset.descriptionViewport = viewport;
    toggle.setAttribute('aria-controls', root.id);
    toggle.setAttribute('aria-expanded', 'false');
    root.insertAdjacentElement('afterend', toggle);

    return toggle;
  };

  const initDescriptionCollapsible = (root) => {
    if (!root || root.dataset.collectionDescriptionCollapsibleReady === 'true') {
      return;
    }

    const collapsedLines = Number.parseInt(root.dataset.descriptionCollapsedLines, 10);

    if (!Number.isFinite(collapsedLines) || collapsedLines < 1) {
      root.dataset.collectionDescriptionCollapsibleReady = 'true';
      return;
    }

    const readMoreLabel = root.dataset.descriptionReadMore || 'Read more';
    const readLessLabel = root.dataset.descriptionReadLess || 'Read less';
    const toggle = createDescriptionToggle(root);
    let expanded = false;
    let animationFrame = null;
    let resizeObserver = null;

    const update = () => {
      animationFrame = null;

      if (!root.isConnected) {
        resizeObserver?.disconnect();
        return;
      }

      const lineHeight = getDescriptionLineHeight(root);

      if (!lineHeight) {
        return;
      }

      const collapsedHeight = lineHeight * collapsedLines;
      root.style.setProperty('--description-collapsed-height', `${collapsedHeight}px`);

      const hasOverflow = root.scrollHeight > collapsedHeight + 1;

      if (!hasOverflow) {
        expanded = false;
      }

      root.classList.toggle('collection-description-root--collapsible', hasOverflow);
      root.classList.toggle('collection-description-root--not-collapsible', !hasOverflow);
      root.classList.toggle('collection-description-root--collapsed', hasOverflow && !expanded);
      root.classList.toggle('collection-description-root--expanded', hasOverflow && expanded);

      toggle.hidden = !hasOverflow;
      toggle.textContent = expanded ? readLessLabel : readMoreLabel;
      toggle.setAttribute('aria-expanded', String(hasOverflow && expanded));
    };

    const scheduleUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame = window.requestAnimationFrame(update);
    };

    toggle.addEventListener('click', () => {
      expanded = !expanded;
      update();
    });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(root);
    } else {
      window.addEventListener('resize', scheduleUpdate, { passive: true });
    }

    document.fonts?.ready.then(scheduleUpdate);
    root.dataset.collectionDescriptionCollapsibleReady = 'true';
    scheduleUpdate();
  };

  const isQuestionElement = (element) => {
    if (!element || element.tagName !== 'P') {
      return false;
    }

    const bold = element.querySelector('strong, b');

    return Boolean(bold && bold.textContent.trim() === element.textContent.trim());
  };

  const createFaqDetails = (question, answerNodes) => {
    const details = document.createElement('details');
    details.className = 'collection-faq-kate__item';

    const summary = document.createElement('summary');
    summary.className = 'collection-faq-kate__summary';
    summary.textContent = question;

    const answer = document.createElement('div');
    answer.className = 'collection-faq-kate__answer rte';

    answerNodes.forEach((node) => {
      answer.appendChild(node.cloneNode(true));
    });

    normalizeExternalLinks(answer);
    details.append(summary, answer);

    return details;
  };

  const splitNodesByBreaks = (container) => {
    const lines = [];
    let current = document.createDocumentFragment();

    const pushCurrent = () => {
      const wrapper = document.createElement('span');
      wrapper.appendChild(current.cloneNode(true));

      if (wrapper.textContent.trim()) {
        lines.push(Array.from(wrapper.childNodes).map((node) => node.cloneNode(true)));
      }

      current = document.createDocumentFragment();
    };

    const walk = (nodes) => {
      nodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
          pushCurrent();
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'P') {
          walk(Array.from(node.childNodes));
          pushCurrent();
          return;
        }

        current.appendChild(node.cloneNode(true));
      });
    };

    walk(Array.from(container.childNodes));
    pushCurrent();

    return lines;
  };

  const buildFaqItemsFromLineBreaks = (source) => {
    const items = [];
    let currentItem = null;

    splitNodesByBreaks(source).forEach((lineNodes) => {
      const lineText = lineNodes.map((node) => node.textContent || '').join('').trim();

      if (!lineText) {
        return;
      }

      if (lineText.endsWith('?')) {
        currentItem = {
          question: lineText,
          answerNodes: [],
        };
        items.push(currentItem);
        return;
      }

      if (!currentItem) {
        return;
      }

      const paragraph = document.createElement('p');
      lineNodes.forEach((node) => paragraph.appendChild(node.cloneNode(true)));
      currentItem.answerNodes.push(paragraph);
    });

    return items.filter((item) => item.answerNodes.length > 0);
  };

  const initFaq = (root) => {
    if (!root || root.dataset.collectionFaqReady === 'true') {
      return;
    }

    if (root.querySelector('details summary')) {
      root.querySelectorAll('details').forEach((details) => {
        details.classList.add('collection-faq-kate__item');
        details.querySelector('summary')?.classList.add('collection-faq-kate__summary');
      });
      normalizeExternalLinks(root);
      root.dataset.collectionFaqReady = 'true';
      return;
    }

    const source = root.querySelector('.metafield-rich_text_field') || root;
    const items = [];
    let currentItem = null;

    Array.from(source.children).forEach((child) => {
      if (isQuestionElement(child)) {
        currentItem = {
          question: child.textContent.trim(),
          answerNodes: [],
        };
        items.push(currentItem);
        return;
      }

      if (currentItem) {
        currentItem.answerNodes.push(child);
      }
    });

    if (!items.length) {
      items.push(...buildFaqItemsFromLineBreaks(source));

      if (!items.length) {
        normalizeExternalLinks(root);
        root.dataset.collectionFaqReady = 'true';
        return;
      }
    }

    root.innerHTML = '';
    items.forEach((item) => {
      root.appendChild(createFaqDetails(item.question, item.answerNodes));
    });

    root.dataset.collectionFaqReady = 'true';
  };

  const findRoots = (scope, selector) => {
    if (!scope) {
      return [];
    }

    const roots = [];

    if (scope instanceof Element && scope.matches(selector)) {
      roots.push(scope);
    }

    roots.push(...scope.querySelectorAll(selector));

    return roots;
  };

  const initCollectionEnhancements = (scope = document) => {
    findRoots(scope, DESCRIPTION_ROOT_SELECTOR).forEach(initDescriptionAccordion);
    findRoots(scope, DESCRIPTION_ROOT_SELECTOR).forEach(initDescriptionCollapsible);
    findRoots(scope, FAQ_ROOT_SELECTOR).forEach(initFaq);
  };

  window.initCollectionEnhancements = initCollectionEnhancements;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCollectionEnhancements(), { once: true });
  } else {
    initCollectionEnhancements();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCollectionEnhancements(event.target);
  });
})();
